import css from "./builderssection.scss?inline";
import globalStyles from "../../index.scss?inline";
import { Component } from "../../utils/Component";
import { ArgSpecDictionary } from "../component-utils";
import { HHDSSegmentedControl } from "../SegmentedControl/SegmentedControl";
import { HHDSParcelsMapEvent, HHDSParcelsMap } from "../ParcelsMap/ParcelsMap";
import { FocusHandler } from "../../utils/FocusHandler";
import {
	HHDSMapPopup,
	HHDSMapPopupEvent,
	HHDSMapPopupHAlign,
	HHDSMapPopupTagName,
	HHDSMapPopupVAlign,
} from "../MapPopup/MapPopup";
import { CmsParcel } from "./BuildersSectionCmsTypes";
import { mapHelper } from "../../utils/maps/map-helper";

const DEBUG_VERBOSE: boolean = false;
const CLASS_NAME: string = "HHDSBuildersSection";
export const HHDSBuildersSectionTagName: string = "hhds-builders-section";
const TAG_NAME: string = HHDSBuildersSectionTagName;

export const HHDSBuildersSectionAttrNames = {
	body: "body",
	header: "header",
	badge: "badge",
	defaultView: "default-view",
	apiKey: "api-key",
	overlay: "overlay",
};

enum HHDSBuildersSectionView {
	map = "map",
	list = "list",
}

const Attrs = HHDSBuildersSectionAttrNames;

export class HHDSBuildersSection extends Component {
	private currentViewType: HHDSBuildersSectionView =
		HHDSBuildersSectionView.map;
	private popup: HHDSMapPopup | null = null;
	private focusHandler!: FocusHandler;

	constructor() {
		super();
	}

	protected override init(): void {
		DEBUG_VERBOSE && console.log(CLASS_NAME, "init");
		// this.vars.set(Attrs.defaultView, HHDSBuildersSectionView.map, "HHDSBuildersSectionView");
		this.currentViewType = this.vars.get<HHDSBuildersSectionView>(
			Attrs.defaultView
		);
		this.render();
	}

	private render(): void {
		const headerText: string = this.vars.get<string>(Attrs.header);
		const bodyText: string = this.vars.get<string>(Attrs.body);
		const badgeText: string = this.vars.get<string>(Attrs.badge);
		const apiKey: string = this.vars.get<string>(Attrs.apiKey);
		const currentViewType: HHDSBuildersSectionView = this.currentViewType;
		const overlay: string = this.vars.get<string>(Attrs.overlay);

		const topHtml = `
    <div class="${TAG_NAME}__top">
      <hhds-richtext><h2 class="eyebrow eyebrow--01">${badgeText}</h2></hhds-richtext>
      <hhds-richtext><h2 class="heading heading--02">${headerText}</h2></hhds-richtext>
      <hhds-richtext><p class="body body--03">${bodyText}</p></hhds-richtext>
      <hhds-segmented-control selected="${currentViewType}">
        <div value="map">Map View</div>
        <div value="list">Grid View</div>
      </hhds-segmented-control>
    </div>`;

		let bottomHtml: string = "";
		switch (currentViewType) {
			case HHDSBuildersSectionView.map:
				bottomHtml = `
          <div class="${TAG_NAME}__main-view-container">
            <div class="${TAG_NAME}__map-view">
              <hhds-parcelsmap key="${apiKey}" image="${overlay}"></hhds-parcelsmap>
              <div class="container">  
                ${mapHelper.popupHtml(
									HHDSMapPopupHAlign.left,
									HHDSMapPopupVAlign.top
								)}
              </div>
            </div>
          </div>`;
				break;
			case HHDSBuildersSectionView.list:
				bottomHtml = /* html */ `
        <div class="container">
          <div class"grid">
            <div class="col-span-6 sm:col-span-8 md:col-span-12 lg:col-span-12">
              <div class="${TAG_NAME}__main-view-container">
                <div class="${TAG_NAME}__list-view">
                  <slot name="buildercards"></slot>
                </div>
              </div>
            </div>
          </div>
        </div>`;
				break;
		}

		this.shadow.innerHTML = /* html */ `
        <style>${globalStyles}</style>
        <style>${css}</style>
        <div class="${TAG_NAME}">
            <div class="container">
                <div class="grid">
                    <div class="col-span-6 sm:col-span-8 md:col-span-8 md:col-start-3 lg:col-span-6 lg:col-start-4">
                        ${topHtml}
                    </div>
                </div>
            </div>
            ${bottomHtml}
        </div>
		`;

		requestAnimationFrame(() => {
			// Request an AOS refresh when the view potentially has a significant height change.
			document.dispatchEvent(new Event("hhds-aos-refresh"));
		});

		const parcelsMap = this.shadow.querySelector(
			"hhds-parcelsmap"
		) as HHDSParcelsMap;
		if (parcelsMap) {
			this.focusHandler = new FocusHandler(parcelsMap, true);

			parcelsMap.addEventListener(HHDSParcelsMapEvent.pan, (_event: any) => {
				if (this.popup) this.popup.ignoreNextHideClick = true;
			});

			parcelsMap.addEventListener(
				HHDSParcelsMapEvent.parcelActivated,
				(event: any) => {
					if (this.popup) {
						this.popup.ignoreNextHideClick = true;
					}

					const parcelId = event.detail?.id;
					const element = event.detail?.element;

					if (element) {
						const parcel = parcelsMap.getParcel(parcelId);
						if (parcel?.selectedViaKeyboard) {
							this.focusHandler.setPreTrapFocusedElement(element);
						}
					}

					this.fetchParcel(parcelId).then((parcel: CmsParcel | null) => {
						if (parcel) this.showMapPopup(parcel);
					});
				}
			);
		}

		const segmentedControl = this.shadow.querySelector(
			"hhds-segmented-control"
		) as HHDSSegmentedControl;
		segmentedControl.value = this.currentViewType;
		segmentedControl?.addEventListener("change", (event: Event) => {
			const control = event.target as HHDSSegmentedControl;
			this.currentViewType = control.value as HHDSBuildersSectionView;
			this.render();
		});

		this.popup = this.shadow.querySelector(HHDSMapPopupTagName) as HHDSMapPopup;
		this.popup?.addEventListener(HHDSMapPopupEvent.show, () => {
			requestAnimationFrame(() => {
				this.focusHandler.updateFocusableElements(
					this.popup?.focusableElements
				);
				this.focusHandler.trap();
			});
		});
		this.popup?.addEventListener(HHDSMapPopupEvent.hide, () => {
			const refocusedElement = this.focusHandler.untrap();
			parcelsMap?.unactivateParcels();
			if (refocusedElement?.dataset.parcelId) {
				parcelsMap?.rehighlightParcelIfRequired(
					refocusedElement.dataset.parcelId
				);
			}
		});

		if (this.popup && parcelsMap) {
			const updatePopupHeight = () => {
				this.popup?.style.setProperty(
					"--map-popup-height",
					`${parcelsMap.clientHeight}px`
				);
			};
			window.addEventListener("resize", () => updatePopupHeight());
			updatePopupHeight();
		}
	}

	private fetchParcel(parcelId: string): Promise<CmsParcel | null> {
		const url: string = `/wp-json/teravalis/v1/parcel/${parcelId}`;
		return new Promise((resolve) => {
			fetch(url)
				.then((response) => response.json())
				.then((data) => {
					resolve(data);
				})
				.catch((error) => {
					console.error("Error fetching data:", error);
					resolve(null);
				});
		});
	}

	private showMapPopup(parcel: CmsParcel): void {
		const slottedHtml: string = this.createParcelCardHtml(parcel);
		this.popup?.show();
		this.popup?.setSlotContent(slottedHtml, "content");

		const requestInfoButton = this.popup?.shadowRoot?.querySelector(
			`.${TAG_NAME}__request-info`
		);
		requestInfoButton?.addEventListener("click", () => {
			document.dispatchEvent(
				new CustomEvent("request-info", {
					detail: { builder: parcel.builder?.title },
				})
			);
		});
	}

	private createParcelCardHtml(parcel: CmsParcel): string {
		const imgSrc: string =
			parcel.collections?.[0]?.image || parcel.builder?.image;

		const body: string =
			parcel.builder?.excerpt || "No builder description available.";
		const title: string = parcel.title;

		const beds = parcel.builder?.beds || "";
		const baths: string = parcel.builder?.baths || "";
		const garage: string = parcel.builder?.garages || "";
		const story: string = parcel.builder?.stories || "";
		const sqft: string = parcel.builder?.sq_ft || "";
		const price: string = parcel.builder?.price || "";

		const hasEmptyField = [beds, baths, garage, story, sqft].some(
			(value) => value === ""
		);

		const willShowHomeInfo: boolean =
			!hasEmptyField && !parcel.builder.coming_soon;

		const buttonStyles: string = `
        --button-width: 100%;
        --button-min-width: 0px;
        --button-wrapper-width: 100%;
        --button-wrapper-padding: 0px;
        --button-hover-offset-x: 0px;
        flex: 1;
    `;

		let buttonsHtml: string = ``;

		if (parcel.builder.coming_soon) {
			//const builderTitle = encodeURIComponent(parcel.builder.hubspot_id);
			//const url = `/subscribe/?select_builder=${builderTitle}`;
			buttonsHtml += `<hhds-button class="${TAG_NAME}__request-info" style="${buttonStyles}" slot="buttons">Request More Info</hhds-button>`;
		} else {
			if (parcel.builder?.url) {
				buttonsHtml += `<hhds-button style="${buttonStyles}" slot="buttons" href="${parcel.builder.url}">Builder Details</hhds-button>`;
			}
			if (parcel.builder?.slug && !hasEmptyField) {
				const homeFinderUrl: string = `/find-your-home/?_builders=${parcel.builder.ID}`;
				buttonsHtml += `<hhds-button style="${buttonStyles}" slot="buttons" href="${homeFinderUrl}">See Collections</hhds-button>`;
			}
		}

		const headingStyle: string = willShowHomeInfo
			? "heading--01"
			: "heading--01";

		const imageHtml: string = imgSrc
			? `<hhds-image src="${imgSrc}" alt="" style="--image-aspect-ratio: 16/9; --image-object-fit: cover;" animate="true" slot="image"></hhds-image>`
			: ``;

		const html: string = `<hhds-card slot="content" type="map">
          ${imageHtml}
					<hhds-richtext style="
						--richtext-padding: var(--spacing-spacer-12);
						--richtext-padding-md: var(--spacing-spacer-16); 
            --richtext-heading-text-align: center;
						--richtext-display: block;
						flex: 1;
					">
						<h3 class="heading ${headingStyle}" >${title}</h3>
						<p class="bodytext bodytext--01">${body}</p>
					</hhds-richtext>
          ${
						willShowHomeInfo
							? `<hhds-home-info type="collection" slot="home-info" type="plans" beds="${beds}" baths="${baths}" garage="${garage}" story="${story}" sqft="${sqft}" price="${price}"></hhds-home-info>`
							: ""
					}
					${buttonsHtml}
        </hhds-card>`;
		return html;
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && console.log(CLASS_NAME, "destroy");
	}

	override onAttributeChanged(
		name: string,
		_oldValue: string,
		newValue: string
	): void {
		DEBUG_VERBOSE &&
			console.log(CLASS_NAME, "Attribute changed: ", name, _oldValue, newValue);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, elements: Element[]): void {
		if (elements.length == 0) {
			DEBUG_VERBOSE && console.log(CLASS_NAME, "Slot emptied");
		} else {
			DEBUG_VERBOSE && console.log(CLASS_NAME, "Slot changed");
		}
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.apiKey]: {
		type: String,
		defaultValue: "",
		description: "API Key",
	},
	[Attrs.header]: {
		type: String,
		defaultValue: "",
		description: "Header text",
	},
	[Attrs.body]: {
		type: String,
		defaultValue: "",
		description: "Body text",
	},
	[Attrs.badge]: {
		type: String,
		defaultValue: "",
		description: "Badge text",
	},
	[Attrs.overlay]: {
		type: String,
		defaultValue: "",
		description: "Overlay image for the map view (as an SVG)",
	},
	[Attrs.defaultView]: {
		type: HHDSBuildersSectionView,
		typeString: "HHDSBuildersSectionView",
		defaultValue: HHDSBuildersSectionView.map,
		description: "Default view",
	},
};
